import { useEffect, RefObject } from 'react'

import usePropsRef from './usePropsRef'

/**
 * Hook that alerts clicks outside of the passed ref
 * @param props - Object with ref, handler and optional isOutside function
 * @param props.ref - Ref to the element to check if clicked outside of
 * @param props.handler - Function to call on outside click
 * @param props.isOutside - Optional function to check if the target is outside of the element
 * @returns void
 * @example
 * const ref = useRef();
 * useOnClickOutside(ref, () => setModalOpen(false));
 */
export default function useOnClickOutside(
  ref: RefObject<HTMLElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
  isOutside?: (target: Node) => boolean
) {
  const hookStorageRef = usePropsRef({ ref, handler, isOutside })

  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const { ref, handler, isOutside } = hookStorageRef.current
      if (
        !ref.current ||
        ref.current?.contains?.(event.target as Node) ||
        (isOutside && !isOutside(event.target as Node))
      ) {
        return
      }
      handler?.(event)
    }

    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)

    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [hookStorageRef])
}
